import React, { useState, useCallback } from 'react'
import styles from './Release.module.scss'
import ReleasePoster from '@/component/Common/ReleasePoster'
import classNames from 'classnames'
import Icon from '@/component/Primitive/Icon'
import CloseIcon from '@/asset/img/redesign24/close.svg'
import { paragraphShorten } from '@/lib/paragraph-shorten'

import ContentAdviceAccordion from './components/ContentAdviceAccordion/ContentAdviceAccordion'

import { Accordion } from './components/Accordion/Accordion'
import MediaVersionsEpisode from '@/component/Common/MediaVersionsEpisode'

import MediaVersions from '@/component/Common/MediaVersions'
import KeyDetails from '@/component/Common/KeyDetails'
import IndustryDetails from './components/IndustryDetails/IndustryDetails'
import ActionButtons from './components/ActionButtons/ActionButtons'
import FilmTimes from '@/component/Common/FilmTimes'
import Trailer from '@/component/Common/Trailer'
import { string, array, number, object, any } from 'prop-types'
import sortBy from 'lodash.sortby'

const Release = (props) => {
  const {
    classification,
    title,
    shortFormInsight,
    synopsis,
    visualAdviceLayer,
    genre,
    durationSeconds,
    videos,
    features,
    releaseYear,
    releaseDate,
    rejectedDate,
    directors,
    sourceUri,
    cast,
    trailers,
    valueAddedMedia,
    classificationDate,
    lang,
    mxfReleaseId,
    trailerYoutubeId,
    images,
    seasons,
    mediaType,
    distributor,
    nonDefiningIssues,
    primaryAsset
  } = props

  const [toggleDescription, setToggleDescription] = useState(false)
  const [toggleFilmTimes, setToggleFilmTimes] = useState(false)
  const [toggleTrailer, setToggleTrailer] = useState(false)
  const primaryAssetNonDefiningIssues =
    (primaryAsset && primaryAsset.nonDefiningIssues) || nonDefiningIssues

  const typeOfMedia =
    primaryAsset && primaryAsset.mediaType && mediaType !== 'TV Show'
      ? primaryAsset.mediaType
      : mediaType

  const shouldShowLongFormInsights =
    (!seasons || !seasons.length) &&
    typeOfMedia !== 'Trailer' &&
    typeOfMedia !== 'Performance'

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setToggleFilmTimes(false)
      setToggleTrailer(false)
    }
  }, [])

  return (
    <>
      <div className={styles.Container}>
        <div className={styles.MainContainer}>
          <div
            className={classNames(styles.TopContent, styles.MobileTopContent)}
          >
            <div className={styles.HeadingContent}>
              <div className={styles.RatingIconWrap}>
                <Icon
                  className={styles.RatingIcon}
                  type={`rating-${classification.toLowerCase()}`}
                  a11yText={`rating-${classification.toLowerCase()}`}
                />
              </div>
              <h1 className={styles.MediaTitle}>{title}</h1>
            </div>
            <div
              className={classNames(
                styles[`Rating-${classification.toLowerCase()}`],
                styles.ShortFormInsight
              )}
            >
              {shortFormInsight}
            </div>
          </div>

          <div className={styles.PosterWrap}>
            <div className={styles.DesktopPosterWrap}>
              <ReleasePoster
                image={images._751 && { alt: title, src: images._751.url }}
                classification={props.classification}
              />
              <div
                className={classNames(
                  styles.ActionButtons,
                  styles.DesktopActionButtons
                )}
              >
                <ActionButtons
                  setToggleFilmTimes={
                    mxfReleaseId ? setToggleFilmTimes : undefined
                  }
                  setToggleTrailer={
                    trailerYoutubeId && !['15', '18'].includes(classification)
                      ? setToggleTrailer
                      : undefined
                  }
                />
              </div>
              <div className={classNames(styles.DesktopFilmDetails)}>
                <KeyDetails
                  genre={genre}
                  duration={durationSeconds}
                  releaseYear={releaseYear}
                  releaseDate={releaseDate}
                  rejectedDate={rejectedDate}
                  sourceUri={sourceUri}
                  directors={primaryAsset?.directors}
                  cast={primaryAsset?.cast}
                  layoutV2
                />
              </div>
            </div>

            <div className={styles.DesktopContent}>
              <div
                className={classNames(
                  styles.TopContent,
                  styles.DesktopTopContent
                )}
              >
                <div className={styles.HeadingContent}>
                  <div className={styles.RatingIconWrap}>
                    <Icon
                      className={styles.RatingIcon}
                      type={`rating-${classification.toLowerCase()}`}
                      a11yText={`rating-${classification.toLowerCase()}`}
                    />
                  </div>
                  <h3 className={styles.MediaTitle}>{title}</h3>
                </div>
                {shortFormInsight && typeOfMedia !== 'TV Show' && (
                  <div
                    className={classNames(
                      styles[`Rating-${classification.toLowerCase()}`],
                      styles.ShortFormInsight
                    )}
                  >
                    {shortFormInsight}
                  </div>
                )}
              </div>
              {synopsis && typeOfMedia !== 'TV Show' && (
                <div
                  className={classNames(
                    styles.Description,
                    styles.MobileDescription
                  )}
                >
                  {toggleDescription ? synopsis : paragraphShorten(synopsis)}
                  <button
                    onClick={() => setToggleDescription(!toggleDescription)}
                    className={styles.ReadMore}
                  >
                    {toggleDescription ? 'Read less' : 'Read more'}
                  </button>
                </div>
              )}

              {synopsis && typeOfMedia !== 'TV Show' && (
                <div
                  className={classNames(
                    styles.Description,
                    styles.DesktopDescription
                  )}
                >
                  {synopsis}
                </div>
              )}
              <div
                className={classNames(
                  styles.AccordionWrap,
                  styles.DesktopAccordionWrap
                )}
              >
                {shouldShowLongFormInsights &&
                  classification &&
                  visualAdviceLayer && (
                    <ContentAdviceAccordion
                      classification={classification}
                      visualAdviceLayer={visualAdviceLayer}
                      primaryAssetNonDefiningIssues={
                        primaryAssetNonDefiningIssues
                      }
                      visibleDefault
                    />
                  )}
                {features && features.length > 0 && (
                  <Accordion title="Cinema">
                    <MediaVersions designV2 details={features} />
                  </Accordion>
                )}
                {videos && videos.length > 0 && (
                  <Accordion title="Home Entertainment">
                    <MediaVersions designV2 details={videos} />
                  </Accordion>
                )}
                {trailers && trailers.length > 0 && (
                  <Accordion title="Trailers">
                    <MediaVersions designV2 details={trailers} />
                  </Accordion>
                )}
                {mediaType === 'TV Show' &&
                  seasons?.length > 0 &&
                  seasons.map((season, index) => {
                    return (
                      <Accordion
                        key={season.number}
                        title={`Season ${season.number}`}
                      >
                        <MediaVersionsEpisode
                          designV2
                          distributor={distributor}
                          details={sortBy(season.episodes, 'episode')}
                          showDuration
                        />
                      </Accordion>
                    )
                  })}
                {valueAddedMedia && valueAddedMedia.length > 0 && (
                  <Accordion title="Bonus Material">
                    <MediaVersions designV2 details={valueAddedMedia} />
                  </Accordion>
                )}
                {lang && classificationDate && (
                  <Accordion title="Industry Details">
                    <IndustryDetails lang={lang} date={classificationDate} />
                  </Accordion>
                )}
              </div>
            </div>
          </div>
          <div
            className={classNames(
              styles.ActionButtons,
              styles.MobileActionButtons
            )}
          >
            <ActionButtons
              setToggleFilmTimes={mxfReleaseId ? setToggleFilmTimes : undefined}
              setToggleTrailer={
                trailerYoutubeId && !['15', '18'].includes(classification)
                  ? setToggleTrailer
                  : undefined
              }
            />
          </div>

          <div
            className={classNames(
              styles.AccordionWrap,
              styles.MobileAccordionWrap
            )}
          >
            {classification && visualAdviceLayer && (
              <ContentAdviceAccordion
                classification={classification}
                visualAdviceLayer={visualAdviceLayer}
                primaryAssetNonDefiningIssues={primaryAssetNonDefiningIssues}
              />
            )}
            {mediaType === 'TV Show' &&
              seasons?.length > 0 &&
              seasons.map((season, index) => (
                <Accordion
                  key={season.number}
                  title={`Season ${season.number}`}
                >
                  <MediaVersions designV2 details={season.episodes} />
                </Accordion>
              ))}
            <Accordion title="Film Details">
              <KeyDetails
                genre={genre}
                duration={durationSeconds}
                releaseYear={releaseYear}
                releaseDate={releaseDate}
                rejectedDate={rejectedDate}
                sourceUri={sourceUri}
                directors={primaryAsset?.directors}
                cast={primaryAsset?.cast}
                layoutV2
              />
            </Accordion>
            {features && features.length > 0 && (
              <Accordion title="Cinema">
                <MediaVersions designV2 details={features} />
              </Accordion>
            )}
            {videos && videos.length > 0 && (
              <Accordion title="Home Entertainment">
                <MediaVersions designV2 details={videos} />
              </Accordion>
            )}
            {trailers && trailers.length > 0 && (
              <Accordion title="Trailers">
                <MediaVersions designV2 details={trailers} />
              </Accordion>
            )}

            {valueAddedMedia && valueAddedMedia.length > 0 && (
              <Accordion title="Bonus Material">
                <MediaVersions designV2 details={valueAddedMedia} />
              </Accordion>
            )}
            {lang && classificationDate && (
              <Accordion title="Industry Details">
                <IndustryDetails lang={lang} date={classificationDate} />
              </Accordion>
            )}
          </div>
        </div>
      </div>
      {toggleFilmTimes && (
        <>
          <div
            className={styles.ModalOverlay}
            onClick={() => setToggleFilmTimes(false)}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
          />
          <div className={styles.Modal}>
            <div className={styles.ModalContainer}>
              <div className={styles.ModalContentContainer}>
                <button
                  onClick={() => setToggleFilmTimes(false)}
                  className={styles.CloseModalButton}
                >
                  <img src={CloseIcon} alt="close" />
                </button>

                <FilmTimes mxfReleaseId={mxfReleaseId} layoutV2 />
              </div>
            </div>
          </div>
        </>
      )}
      {toggleTrailer &&
        (mxfReleaseId || trailerYoutubeId) &&
        !['15', '18'].includes(classification) && (
          <>
            <div
              className={styles.ModalOverlay}
              onKeyDown={handleKeyDown}
              onClick={() => setToggleTrailer(false)}
              tabIndex={0}
              role="button"
            />
            <div className={styles.Modal}>
              <div className={styles.ModalContainer}>
                <div className={styles.ModalContentContainer}>
                  <button
                    onClick={() => setToggleTrailer(false)}
                    className={styles.CloseModalButton}
                  >
                    <img src={CloseIcon} alt="close" />
                  </button>
                  <div className={styles.ModalTitle}>Trailer</div>
                  <Trailer
                    headingStyles={styles.Heading}
                    videoWrapperStyles={styles.Video}
                    hlsStreamUrl={`https://film-cdn.moviexchange.com/api/cdn/release/${mxfReleaseId}/media/TrailerVideo?width=0&height=0`}
                    youtubeVideoId={trailerYoutubeId}
                  />
                </div>
              </div>
            </div>
          </>
        )}
    </>
  )
}
Release.propTypes = {
  classification: string,
  title: string,
  shortFormInsight: string,
  synopsis: string,
  visualAdviceLayer: array,
  genre: array,
  durationSeconds: number,
  videos: array,
  features: array,
  releaseYear: number,
  releaseDate: any,
  rejectedDate: any,
  directors: array,
  sourceUri: string,
  cast: array,
  trailers: array,
  valueAddedMedia: array,
  classificationDate: any,
  lang: string,
  mxfReleaseId: string,
  trailerYoutubeId: string,
  images: object,
  seasons: array,
  mediaType: string,
  distributor: string,
  nonDefiningIssues: string,
  primaryAsset: {
    nonDefiningIssues: string
  }
}
export default Release
